import React, { Component } from 'react';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Menu } from 'primereact/menu';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { useNavigate } from 'react-router-dom';
import ClassetStudentSearchFilter from '../Search/ClassetStudentSearchFilter';
import { getDefaultState, getMenuItems } from './constants';
import { getInitials, formatDate, normalizeStatus, generateDailyAttendanceData } from './utils';
import { renderAttendanceInfoView, renderDailyAttendanceContent } from './attendanceHelpers';
import { renderStudentOtherDetailsView, renderParentDetailsView, renderGuardianDetailsView, renderSiblingsInfoView, renderTransportInfoView } from './detailViews';
import { renderPersonalTab, renderContactTab, renderAcademicTab, renderOtherTab } from './tabViews';
import { fetchStudentData, fetchAttendanceData, exportAttendanceToExcel, exportDailyAttendanceToExcel, fetchFeetypeTerms, fetchFeeHistory, fetchTransactions, fetchCancelledTransactions } from './apiService';
import { groupAndSumFeesByType } from './utils';
import { 
    StudentSidebar, 
    StudentDetailsSection, 
    StudentNavigation, 
    StudentDetailsView, 
    FinanceView 
} from './components';
import { 
    renderReceiptEntryView, 
    renderFeeDetailsView, 
    renderViewTransactionsView, 
    renderFeeManagementView,
    renderViewChequePaymentsView,
    renderTransportFeeManagementView,
    renderChequeBounceChargesView,
    renderPostDatedChequePaymentsView,
    renderAdvancePaymentsView,
    renderEditFeeAmountView,
    renderStudentDepositsTransactionsView,
    renderLoanPaymentsView
} from './financeViews';
import './studentProfile.scss';

class StudentProfile extends Component {
    constructor(props) {
        super(props);
        this.studentSearchRef = React.createRef();
        this.studentFormsMenu = React.createRef();
        this.studentLogMenu = React.createRef();
        this.academicsMenu = React.createRef();
        this.certificatesMenu = React.createRef();
        this.toast = React.createRef();
        this.state = getDefaultState();
    }

    componentDidMount() {
        // store initial search string and if a studentId was supplied via query params,
        // trigger the same selection flow used by the search filter
        try {
            const currentSearch = window.location.search || '';
            this._lastSearch = currentSearch;
            const params = new URLSearchParams(currentSearch);
            const sid = params.get('studentId');
            if (sid) {
                this.setDropdownValues({ studentIds: [sid] });
            }
        } catch (e) {
            // ignore
        }
    }

    setDropdownValues = (data) => {
        // Handle data from ClassetStudentSearchFilter (array of single object with studentId)
        if (data?.studentIds && data?.studentIds?.length > 0) {
            const studentId = data.studentIds[0];
            
            // Update the visual selection in the search filter if ref exists
            // Use multiple retries with increasing delays to ensure the ref is mounted and students are loaded
            const updateSearchFilter = (retryCount = 0) => {
                if (this.studentSearchRef?.current?.setSelectedStudents) {
                    this.studentSearchRef.current.setSelectedStudents(data.studentIds);
                } else if (retryCount < 20) { // Retry for up to 2 seconds
                    setTimeout(() => updateSearchFilter(retryCount + 1), 100);
                }
            };
            
            updateSearchFilter();
            
            this.setState({
                studentId: studentId
            }, () => {
                this.handleFetchStudentData(studentId);
            });
        } else {
            // Student search cleared - reset to default state
            const defaultState = getDefaultState();
            this.setState({
                ...defaultState,
                studentId: '',
                showDetailView: false
            });
        }
    }

    handleFetchStudentData = (studentId) => {
        const academicYear = localStorage.getItem('userAcademicYear');
        fetchStudentData(
            studentId,
            academicYear,
            this.toast,
            this.setState.bind(this),
            (sid, ay) => this.handleFetchAttendanceData(sid, ay)
        );
    }

    handleFetchAttendanceData = (studentId, academicYear) => {
        fetchAttendanceData(
            studentId,
            academicYear,
            this.toast,
            this.setState.bind(this)
        );
    }

    handleFetchFeetypeData = async (studentId, academicYear) => {
        if (!studentId || !academicYear) {
            return;
        }

        this.setState({ feetypeWiseFeeLoading: true });

        try {
            const data = await fetchFeetypeTerms(studentId, academicYear);
            
            if (data && data.agg) {
                const transformedData = groupAndSumFeesByType(data.agg);
                this.setState({ 
                    feetypeWiseFeeData: transformedData,
                    feeTermsRawData: data.agg, // Store raw data for other views
                    feetypeWiseFeeLoading: false 
                });
            } else {
                this.setState({ 
                    feetypeWiseFeeData: null,
                    feeTermsRawData: null,
                    feetypeWiseFeeLoading: false 
                });
            }
        } catch (error) {
            this.setState({ 
                feetypeWiseFeeData: null,
                feeTermsRawData: null,
                feetypeWiseFeeLoading: false 
            });
            if (this.toast.current) {
                this.toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to fetch fee details',
                    life: 3000
                });
            }
        }
    }

    handleFetchFeeHistory = async (studentId, academicYear) => {
        if (!studentId || !academicYear) {
            return;
        }

        this.setState({ feeHistoryLoading: true });

        try {
            const data = await fetchFeeHistory(studentId, academicYear, this.toast);
            
            if (data) {
                this.setState({ 
                    feeHistoryData: data,
                    feeHistoryLoading: false 
                });
            } else {
                this.setState({ 
                    feeHistoryData: null,
                    feeHistoryLoading: false 
                });
            }
        } catch (error) {
            this.setState({ 
                feeHistoryData: null,
                feeHistoryLoading: false 
            });
            if (this.toast.current) {
                this.toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to fetch fee history',
                    life: 3000
                });
            }
        }
    }

    handleFetchTransactions = async (studentId, academicYear) => {
        if (!studentId || !academicYear) {
            return;
        }

        this.setState({ transactionsLoading: true });

        try {
            const data = await fetchTransactions(studentId, academicYear, this.toast);
            
            if (data) {
                this.setState({ 
                    transactionsData: data,
                    transactionsLoading: false 
                });
            } else {
                this.setState({ 
                    transactionsData: null,
                    transactionsLoading: false 
                });
            }
        } catch (error) {
            this.setState({ 
                transactionsData: null,
                transactionsLoading: false 
            });
            if (this.toast.current) {
                this.toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to fetch transactions',
                    life: 3000
                });
            }
        }
    }

    handleFetchCancelledTransactions = async (studentId, academicYear) => {
        console.log('handleFetchCancelledTransactions called with:', studentId, academicYear);
        if (!studentId || !academicYear) {
            console.log('Missing parameters - returning');
            return;
        }

        this.setState({ cancelledTransactionsLoading: true });
        console.log('Fetching cancelled transactions...');

        try {
            const data = await fetchCancelledTransactions(studentId, academicYear, this.toast);
            console.log('Cancelled transactions data received:', data);
            
            if (data) {
                this.setState({ 
                    cancelledTransactionsData: data,
                    cancelledTransactionsLoading: false 
                });
            } else {
                this.setState({ 
                    cancelledTransactionsData: null,
                    cancelledTransactionsLoading: false 
                });
            }
        } catch (error) {
            this.setState({ 
                cancelledTransactionsData: null,
                cancelledTransactionsLoading: false 
            });
            if (this.toast.current) {
                this.toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to fetch cancelled transactions',
                    life: 3000
                });
            }
        }
    }

    componentDidUpdate(prevProps, prevState) {
        // Detect route query change (e.g., browser back navigation) and react to a studentId query param
        try {
            const currentSearch = window.location.search || '';
            if (this._lastSearch !== currentSearch) {
                this._lastSearch = currentSearch;
                const params = new URLSearchParams(currentSearch);
                const sid = params.get('studentId');
                if (sid && sid !== this.state.studentId) {
                    // Programmatically trigger the same flow ClassetStudentSearchFilter uses
                    this.setDropdownValues({ studentIds: [sid] });
                }
            }
        } catch (e) {
            // ignore parsing errors
        }

        const { studentData, activeFinanceView, showFinanceView } = this.state;
        const academicYear = studentData?.academicYear || localStorage.getItem('userAcademicYear');
        
        // Note: Feetype data is now fetched when Finance menu is clicked (in handleMenuClick)
        // This ensures data is available for all finance sub-menus without redundant API calls
        
        // Fetch transactions data only when:
        // 1. Finance menu is newly opened (handled by handleMenuClick)
        // 2. Student changes while finance view is active
        // 3. Academic year changes while finance view is active
        const shouldRefetchTransactionsForStudentChange = 
            showFinanceView &&
            studentData?.userId &&
            academicYear &&
            (
                (prevState.studentData?.userId !== studentData?.userId) ||
                (prevState.studentData?.academicYear !== studentData?.academicYear)
            );

        if (shouldRefetchTransactionsForStudentChange) {
            this.handleFetchTransactions(studentData.userId, academicYear);
            this.handleFetchFeetypeData(studentData.userId, academicYear);
        }

        // Fetch cancelled transactions when cancelled tab is clicked
        if (this.state.activeTransactionsTab === 'cancelled' && prevState.activeTransactionsTab !== 'cancelled') {
            console.log('Cancelled tab clicked - fetching data');
            if (studentData?.userId && academicYear) {
                console.log('Calling handleFetchCancelledTransactions with:', studentData.userId, academicYear);
                this.handleFetchCancelledTransactions(studentData.userId, academicYear);
            } else {
                console.log('Missing data - userId:', studentData?.userId, 'academicYear:', academicYear);
            }
        }
    }

    getMenuItemsConfig = () => {
        const { activeSubMenu } = this.state;
        return getMenuItems(activeSubMenu, this.setState.bind(this));
    }

    renderPersonalTab = () => {
        const { studentData } = this.state;
        return renderPersonalTab(studentData);
    }

    renderContactTab = () => {
        const { studentData } = this.state;
        return renderContactTab(studentData);
    }

    renderAcademicTab = () => {
        const { studentData } = this.state;
        return renderAcademicTab(studentData);
    }

    renderOtherTab = () => {
        const { studentData } = this.state;
        return renderOtherTab(studentData);
    }



    handleAttendanceTabChange = (tabName) => {
        this.setState({ activeAttendanceTab: tabName });
    }

    handleRefreshAttendance = (month) => {
        const { studentId } = this.state;
        const academicYear = localStorage.getItem('userAcademicYear');

        if (studentId) {
            this.handleFetchAttendanceData(studentId, academicYear);
        }

        this.toast.current?.show({
            severity: 'info',
            summary: 'Refreshing',
            detail: `Updating attendance data for ${month}`,
            life: 3000
        });
    }

    handleUpdateAllAttendance = () => {
        const { studentId } = this.state;
        const academicYear = localStorage.getItem('userAcademicYear');

        if (studentId) {
            this.toast.current?.show({
                severity: 'info',
                summary: 'Updating',
                detail: 'Refreshing all attendance data...',
                life: 3000
            });

            this.handleFetchAttendanceData(studentId, academicYear);
        }
    }

    handleViewDailyAttendance = (month, attendanceType) => {
        const { attendanceData } = this.state;
        const typeData = attendanceData[attendanceType];
        const monthData = typeData?.months?.find(m => m.month === month);

        let dailyData = [];

        if (monthData && monthData.dailyAttendance && monthData.dailyAttendance.length > 0) {
            dailyData = monthData.dailyAttendance.map(day => {
                // Determine the correct status by prioritizing boolean flags over status string
                let correctedStatus = day.status;
                
                // First check if boolean flags are explicitly set (indicates actual attendance was marked)
                // This takes priority over WeekOff/Holiday status (means the day was overridden)
                if (day.isPresent === true) {
                    correctedStatus = 'Present';
                } else if (day.isAbsent === true) {
                    correctedStatus = 'Absent';
                } else if (day.isLate === true) {
                    correctedStatus = 'Late';
                } else if (day.isHalfday === true) {
                    correctedStatus = 'HalfDay';
                }
                // If all boolean flags are false but status says "Absent", treat as NoData
                else if (day.status === 'Absent' && 
                         day.isPresent === false && 
                         day.isAbsent === false && 
                         day.isLate === false && 
                         day.isHalfday === false) {
                    correctedStatus = 'NoData';
                }
                // Check if this is a special status (WeekOff/Holiday) only if no attendance was marked
                else if (day.status === 'WeekOff' || day.status === 'Holiday') {
                    correctedStatus = day.status;
                }
                // Otherwise keep the original status
                
                return {
                    date: formatDate(day.date),
                    day: day.day,
                    status: normalizeStatus(correctedStatus)
                };
            });
        } else {
            // No daily attendance data available - generate default empty data
            dailyData = generateDailyAttendanceData(month, attendanceType);
        }

        this.setState({
            showDailyAttendanceDialog: true,
            selectedMonth: month,
            selectedAttendanceType: attendanceType,
            dailyAttendanceData: dailyData
        });
    }

    closeDailyAttendanceDialog = () => {
        this.setState({
            showDailyAttendanceDialog: false,
            selectedMonth: null,
            selectedAttendanceType: null,
            dailyAttendanceData: []
        });
    }

    handleExportAttendanceToExcel = () => {
        exportAttendanceToExcel(this.state, this.toast);
    }

    handleExportDailyAttendanceToExcel = () => {
        exportDailyAttendanceToExcel(this.state, this.toast);
    }

    renderDetailView = () => {
        const { activeDetailView, studentData } = this.state;

        switch (activeDetailView) {
            case 'personalInfo':
                return renderStudentOtherDetailsView(studentData);
            case 'siblingsInfo':
                return renderSiblingsInfoView(studentData);
            case 'familyDetails':
                return (
                    <>
                        {renderParentDetailsView(studentData)}
                        <Divider style={{ margin: '30px 0' }} />
                        {renderGuardianDetailsView(studentData)}
                    </>
                );
            case 'transportInfo':
                return renderTransportInfoView(studentData);
            case 'attendanceInfo':
                return renderAttendanceInfoView(
                    this.state,
                    {
                        handleRefreshAttendance: this.handleRefreshAttendance,
                        handleUpdateAllAttendance: this.handleUpdateAllAttendance,
                        handleViewDailyAttendance: this.handleViewDailyAttendance,
                        handleExportAttendanceToExcel: this.handleExportAttendanceToExcel,
                        setState: (updates) => this.setState(updates)
                    }
                );
            default:
                return renderStudentOtherDetailsView(studentData);
        }
    }

    getInitials = (name) => {
        return getInitials(name);
    }

    getMenuItems = () => {
        return this.getMenuItemsConfig();
    }

    renderFinanceView = () => {
        const { activeFinanceView, studentData } = this.state;
        const { navigate } = this.props;
        const academicYear = studentData?.academicYear || localStorage.getItem('userAcademicYear');

        switch (activeFinanceView) {
            case 'receiptEntry':
                return renderReceiptEntryView(studentData);
            case 'feeDetails':
                return renderFeeDetailsView(studentData, this.state, this.setState.bind(this), navigate);
            case 'viewTransactions':
                return renderViewTransactionsView(
                    studentData, 
                    this.state, 
                    this.setState.bind(this), 
                    this.toast, 
                    academicYear,
                    this.state.transactionsData,
                    this.state.transactionsLoading,
                    this.state.cancelledTransactionsData,
                    this.state.cancelledTransactionsLoading
                );
            case 'feeManagement':
                return renderFeeManagementView(studentData);
            case 'viewChequePayments':
                return renderViewChequePaymentsView(
                    studentData,
                    this.state,
                    this.setState.bind(this),
                    this.toast,
                    academicYear,
                    this.state.transactionsData,
                    this.state.transactionsLoading
                );
            case 'transportFeeManagement':
                return renderTransportFeeManagementView(
                    studentData, 
                    false, 
                    this.toast, 
                    navigate, 
                    this.state.feeTermsRawData, 
                    this.state.feetypeWiseFeeLoading
                );
            case 'chequeBounceCharges':
                return renderChequeBounceChargesView(
                    studentData,
                    this.state,
                    this.setState.bind(this),
                    this.toast,
                    academicYear,
                    this.state.transactionsData,
                    this.state.transactionsLoading
                );
            case 'postDatedChequePayments':
                return renderPostDatedChequePaymentsView(
                    studentData, 
                    this.state, 
                    this.setState.bind(this),
                    this.state.transactionsData,
                    this.state.transactionsLoading
                );
            case 'advancePayments':
                return renderAdvancePaymentsView(
                    studentData,
                    this.state,
                    this.setState.bind(this),
                    this.toast,
                    academicYear,
                    this.state.transactionsData,
                    this.state.transactionsLoading
                );
            case 'editFeeAmount':
                return renderEditFeeAmountView(studentData);
            case 'studentDepositsTransactions':
                return renderStudentDepositsTransactionsView(studentData);
            case 'loanPayments':
                return renderLoanPaymentsView(studentData);
            default:
                return renderReceiptEntryView(studentData);
        }
    }

    handleMenuClick = (newState) => {
        // Update state with the new menu selection
        this.setState(newState);

        // If Finance menu is clicked, fetch both transactions and feetype terms data
        if (newState.activeMenu === 'finance' && newState.showFinanceView) {
            const { studentData, transactionsData, feeTermsRawData, feeHistoryData } = this.state;
            const academicYear = studentData?.academicYear || localStorage.getItem('userAcademicYear');
            
            // Fetch transactions data if not already loaded
            if (studentData?.userId && academicYear && !transactionsData) {
                this.handleFetchTransactions(studentData.userId, academicYear);
            }

            // Fetch feetype terms data if not already loaded
            if (studentData?.userId && academicYear && !feeTermsRawData) {
                this.handleFetchFeetypeData(studentData.userId, academicYear);
            }

            // Fetch fee history data if not already loaded (for Month Wise, Refund Details, Concessions tabs)
            if (studentData?.userId && academicYear && !feeHistoryData) {
                this.handleFetchFeeHistory(studentData.userId, academicYear);
            }
        }
    }

    render() {
        const { studentData, activeIndex, showDetailView, activeMenu } = this.state;
        const initials = this.getInitials(`${studentData.firstName} ${studentData.lastName}`);
        const menuItems = this.getMenuItems();

        // Check if student data is available
        const hasStudentData = studentData && (studentData.firstName || studentData.studentId || studentData.enrollmentId);

        return (
            <div className="student-profile-container">


                {/* Search Filter Section */}
                <div className="student-search-section">
                    <ClassetStudentSearchFilter
                        ref={this.studentSearchRef}
                        setValues={this.setDropdownValues}
                        singleSelectInput={true}
                    />
                </div>

                {/* Show Profile Layout only when student data is available */}
                {hasStudentData && (
                    <>
                        <div className="student-profile-layout">
                            {/* Left Sidebar */}
                            <StudentSidebar studentData={studentData} />

                            {/* Right Content Area */}
                            <StudentDetailsSection
                                activeIndex={activeIndex}
                                onTabChange={(e) => this.setState({ activeIndex: e.index })}
                                renderPersonalTab={this.renderPersonalTab}
                                renderContactTab={this.renderContactTab}
                                renderAcademicTab={this.renderAcademicTab}
                                renderOtherTab={this.renderOtherTab}
                            />
                        </div>

                        {/* Navigation Menu */}
                        <StudentNavigation
                            activeMenu={activeMenu}
                            menuItems={menuItems}
                            studentFormsMenuRef={this.studentFormsMenu}
                            studentLogMenuRef={this.studentLogMenu}
                            academicsMenuRef={this.academicsMenu}
                            certificatesMenuRef={this.certificatesMenu}
                            onMenuClick={(newState) => this.handleMenuClick(newState)}
                            onDropdownMenuClick={() => this.setState({ showFinanceView: false, showStudentDetailsView: false })}
                        />

                        {/* Student Details View Section - Shows with left menu panel */}
                        {this.state.showStudentDetailsView && (
                            <StudentDetailsView
                                activeSubMenu={this.state.activeSubMenu}
                                studentDetailsMinimized={this.state.studentDetailsMinimized}
                                onMenuItemClick={(newState) => this.setState(newState)}
                                onToggleMinimize={() => this.setState({ studentDetailsMinimized: !this.state.studentDetailsMinimized })}
                                renderDetailView={this.renderDetailView}
                            />
                        )}

                        {/* Finance View Section - Shows with left menu panel */}
                        {this.state.showFinanceView && (
                            <FinanceView
                                activeSubMenu={this.state.activeSubMenu}
                                financeMinimized={this.state.financeMinimized}
                                onMenuItemClick={(newState) => this.setState(newState)}
                                onToggleMinimize={() => this.setState({ financeMinimized: !this.state.financeMinimized })}
                                onFeeDetailsClick={() => {
                                    this.setState({ showFinanceView: true, activeFinanceView: 'feeDetails', activeSubMenu: 'feeDetails' });
                                }}
                                renderFinanceView={this.renderFinanceView}
                            />
                        )}
                    </>
                )}

                {/* Daily Attendance Dialog */}
                <Dialog
                    header={`As per Daily Attendance`}
                    visible={this.state.showDailyAttendanceDialog}
                    onHide={this.closeDailyAttendanceDialog}
                    style={{ width: '80vw', maxWidth: '1000px' }}
                    modal
                    className="daily-attendance-dialog"
                >
                    {renderDailyAttendanceContent(this.state, this.handleExportDailyAttendanceToExcel)}
                </Dialog>

                <Toast ref={this.toast} position="bottom-right" />
            </div>
        );
    }
}

// HOC wrapper to provide navigate prop from useNavigate hook
const StudentProfileWithNavigate = (props) => {
    const navigate = useNavigate();
    return <StudentProfile {...props} navigate={navigate} />;
};

export default StudentProfileWithNavigate;
